// The Supabase side of syncBlob — kept separate so the merge logic stays a pure,
// trivially testable module.
//
// All seven blob tables share the shape `{user_id, data, updated_at}` with a
// unique constraint on user_id, so one transport covers them all.

import { supabase } from './supabase';
import { syncManager } from './syncManager';
import type { BlobTransport, BlobSnapshot, BlobWriteResult } from './syncBlob';

export function supabaseBlobTransport(table: string): BlobTransport {
  return {
    async read(): Promise<BlobSnapshot | null> {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;
      const { data, error } = await supabase
        .from(table)
        .select('updated_at, data')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      // No row yet is NOT the same as signed out — the caller must still build
      // and insert, or a brand-new account would never get its first write.
      if (!data) return { updatedAt: null, data: null, user: user.id };
      return { updatedAt: (data as any).updated_at ?? null, data: (data as any).data ?? null, user: user.id };
    },

    // A refresh's "has anything moved?" — the timestamp alone, not the blob.
    async version(): Promise<{ updatedAt: string | null } | null> {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;
      const { data, error } = await supabase
        .from(table)
        .select('updated_at')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return { updatedAt: (data as { updated_at?: string | null } | null)?.updated_at ?? null };
    },

    async write(payload: any, expectedUpdatedAt: string | null, forUser?: string): Promise<BlobWriteResult> {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return 'skipped'; // signed out mid-cycle — stop, don't spin on retries
      // ⚠️ Another account signed in since the row was read: the payload was merged
      // into the LAST account's row — it must never become the new account's.
      if (forUser !== undefined && user.id !== forUser) return 'skipped';
      const stamp = new Date().toISOString();

      if (expectedUpdatedAt === null) {
        const { error } = await supabase
          .from(table)
          .insert({ user_id: user.id, data: payload, updated_at: stamp });
        // 23505 = another device inserted the row first; re-read and merge onto it.
        if (error) {
          if ((error as any).code === '23505') return false;
          throw error;
        }
        return true;
      }

      // Compare-and-swap: only overwrite the exact revision we merged against.
      // A concurrent writer moves updated_at, we match 0 rows, and the caller
      // re-reads — which is what stops one device clobbering another's records.
      const { data, error } = await supabase
        .from(table)
        .update({ data: payload, updated_at: stamp })
        .eq('user_id', user.id)
        .eq('updated_at', expectedUpdatedAt)
        .select('user_id, updated_at');
      if (error) throw error;
      // The revision the row is now at, so a refresh knows this tab already holds it.
      const hit = (data as { updated_at?: string | null }[] | null)?.[0];
      return hit ? { updatedAt: hit.updated_at ?? null } : false;
    },
  };
}
